import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, InputAdornment, MenuItem, Pagination, Select, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import HubSectionContainer from '../CustomContainer';
import LessonsHeader from './LessonsHeader';
import { listLessons } from '../../Lesson';
import { useHubPetParent } from '../HubPetParentContext';
import { getPrimaryAnimal } from '../Home/utils';
import { track } from '../analytics';
import useHubPageView from '../useHubPageView';
import useLessonBookmarks from './useLessonBookmarks';
import { sortBookmarkedFirst } from './bookmarkState';
import {
  categoryOptions, forSpecies, paginate, searchLessons, TabSpecies,
} from './catalogFilters';
import LessonCard from './LessonCard';
import BookmarkFeedback from './BookmarkFeedback';

const PAGE_SIZE = 9;
const EMPTY_COPY = 'No lessons match your search. Try a different word.';
const GRID_PATH = '/hub/lessons-and-guides';

export default function LessonsLanding() {
  useHubPageView();
  const navigate = useNavigate();
  const { petParent } = useHubPetParent();
  const bookmarkApi = useLessonBookmarks();
  const { bookmarks, isBookmarked, toggle } = bookmarkApi;

  const primarySpecies: TabSpecies = getPrimaryAnimal(petParent)?.animalType?.toLowerCase() === 'cat'
    ? 'cat'
    : 'dog';

  // Filter state lives in the query string, not component state, so a refresh
  // keeps the tab a parent is on and the in-lesson Back button returns them to
  // the same species, category, search and page they left.
  const [params, setParams] = useSearchParams();
  const speciesParam = params.get('species');
  const species: TabSpecies = speciesParam === 'cat' || speciesParam === 'dog'
    ? speciesParam
    : primarySpecies;
  const category = params.get('category') || 'all';
  const query = params.get('q') || '';
  const page = Math.max(1, Number(params.get('page')) || 1);

  // Patch the query string without stacking history entries for every keystroke:
  // filter changes replace, so Back still leaves the page rather than rewinding
  // through each edit.
  const patchParams = React.useCallback((patch: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([key, value]) => {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      });
      return next;
    }, { replace: true });
  }, [setParams]);

  // Bookmark-first ordering is a SNAPSHOT: it refreshes when the species tab
  // changes and on mount (i.e. returning from a lesson), never while toggling on
  // the grid, so a card never jumps out from under the tap — AC 6 / QA 2.4.
  const [sortKey, setSortKey] = React.useState<Set<string>>(() => new Set(bookmarks));
  const bookmarksRef = React.useRef(bookmarks);
  bookmarksRef.current = bookmarks;
  React.useEffect(() => { setSortKey(new Set(bookmarksRef.current)); }, [species]);

  // Bookmarks arrive from GraphQL after first paint, so the snapshot above starts
  // empty and a hard refresh would show nothing pinned. Seed it once the first
  // real list lands; later toggles still only affect the next snapshot.
  const seededSort = React.useRef(false);
  React.useEffect(() => {
    if (seededSort.current || !bookmarks.size) return;
    seededSort.current = true;
    setSortKey(new Set(bookmarks));
  }, [bookmarks]);

  const catalog = React.useMemo(() => listLessons(), []);
  const options = React.useMemo(() => categoryOptions(catalog, species), [catalog, species]);

  const results = React.useMemo(() => {
    let out = forSpecies(catalog, species);
    if (category !== 'all') out = out.filter((l) => l.category === category);
    out = searchLessons(out, query);
    return sortBookmarkedFirst(out, (l) => l.slug, sortKey, (l) => l.title);
  }, [catalog, species, category, query, sortKey]);

  const totalPages = Math.ceil(results.length / PAGE_SIZE);
  const pageItems = paginate(results, page, PAGE_SIZE);

  // Debounced so one event fires when the parent pauses, not per keystroke.
  React.useEffect(() => {
    if (!query.trim()) return undefined;
    const t = setTimeout(() => track('search_text', { search_text: query.trim() }), 800);
    return () => clearTimeout(t);
  }, [query]);

  const changeSpecies = (next: TabSpecies) => {
    patchParams({ species: next, category: null, page: null });
    track('lessons_filter_tab_click', { pet_type: next });
  };

  const openLesson = (slug: string) => {
    navigate(`${GRID_PATH}/${slug}`, {
      // Carry the live query string so Back restores the exact result set.
      state: { from: params.toString() ? `${GRID_PATH}?${params}` : GRID_PATH, fromWhere: 'lessons_and_guides' },
    });
  };

  const pillSx = (active: boolean) => ({
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: '18px',
    letterSpacing: '0.16px',
    borderRadius: '100px',
    textTransform: 'none' as const,
    px: '14px',
    py: '7px',
    minWidth: 'unset',
    color: active ? '#fff' : 'black !important',
    bgcolor: active ? 'rgba(55, 0, 179, 1)' : 'rgba(0, 0, 0, 0.08)',
    '&:hover': { bgcolor: active ? 'rgba(55, 0, 179, 0.9)' : 'rgba(0, 0, 0, 0.12)' },
  });

  return (
    <>
      <LessonsHeader />

      <HubSectionContainer wide sx={{ pt: 3, pb: 3 }}>
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          mb: 4,
        }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => changeSpecies('dog')} sx={pillSx(species === 'dog')}>Dogs</Button>
            <Button onClick={() => changeSpecies('cat')} sx={pillSx(species === 'cat')}>Cats</Button>
          </Box>

          <Select
            value={category}
            onChange={(e) => patchParams({ category: e.target.value === 'all' ? null : e.target.value, page: null })}
            size="small"
            inputProps={{ 'aria-label': 'Filter by category' }}
            startAdornment={(
              <InputAdornment position="start">
                <FilterAltIcon fontSize="small" sx={{ color: 'rgba(55, 0, 179, 1)' }} />
              </InputAdornment>
            )}
            sx={{ minWidth: 240, borderRadius: '100px', bgcolor: '#fff' }}
          >
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>

          <TextField
            value={query}
            onChange={(e) => patchParams({ q: e.target.value, page: null })}
            placeholder="Search…"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'rgba(0,0,0,0.54)' }} />
                </InputAdornment>
              ),
              sx: { borderRadius: '100px', bgcolor: '#fff' },
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
        </Box>

        {results.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '16px' }}>{EMPTY_COPY}</Typography>
          </Box>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 3,
          }}
          >
            {pageItems.map((lesson) => (
              <LessonCard
                key={lesson.slug}
                lesson={lesson}
                bookmarked={isBookmarked(lesson.slug)}
                onOpen={openLesson}
                onToggleBookmark={toggle}
              />
            ))}
          </Box>
        )}

        {totalPages > 1 && (
          <Box sx={{
            display: 'flex', justifyContent: 'center', mt: 4, mb: 2,
          }}
          >
            <Pagination
              count={totalPages}
              page={page}
              // Paging from the controls at the bottom would otherwise land the
              // parent at the bottom of the next page.
              onChange={(_, value) => {
                patchParams({ page: value === 1 ? null : String(value) });
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              size="small"
              sx={{
                '& .MuiPaginationItem-root': {
                  height: 26,
                  minWidth: 26,
                  fontSize: '14px',
                  borderRadius: '40px',
                  color: 'black',
                  '@media (max-width: 768px)': { height: 40, minWidth: 40 },
                },
                '& .MuiPaginationItem-root.Mui-selected': {
                  bgcolor: 'rgba(0,0,0,0.08)',
                  color: 'black',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.12)' },
                },
              }}
            />
          </Box>
        )}
      </HubSectionContainer>

      <BookmarkFeedback bookmarks={bookmarkApi} />
    </>
  );
}
